import { useState, useCallback, useRef } from 'react';

import { UserDropdownMenu } from './UserDropdownMenu';
import { UserDropdownTrigger } from './UserDropdownTrigger';

import type { UserDropdownProps } from './UserDropdown.types';
import type { NavItem } from '@personal-trainer-app/shared/src/config/nav.config';

import { RefinePlanModal } from '@/components/questionnaire';
import { BottomSheetConnector } from '@/components/ui';
import { useBreakpoint } from '@/hooks';
import { InviteModal } from '@/layouts/components/InviteModal';

/** Rows this component answers itself rather than reporting to the caller. */
const SIGN_OUT = 'sign-out';
const ADJUST_GOALS = 'adjust-goals';
const INVITE = 'invite';

/**
 * UserDropdown component - displays a dropdown menu triggered by user avatar.
 *
 * Features:
 * - Desktop: 240px dropdown anchored below avatar, right-aligned
 * - Mobile (<480px): Bottom sheet with connector beam animation
 * - Header shows avatar + username + online status
 * - Items, grouping and role gates from the shared nav manifest
 * - Sign out button with danger styling
 * - Keyboard shortcuts displayed on desktop (hidden on mobile)
 *
 * @example
 * ```tsx
 * <UserDropdown
 *   user={{ username: 'john_doe', avatar: '/avatars/john.jpg' }}
 *   role="client"
 *   isOnline
 *   onNavigate={(href) => navigate(href)}
 *   onSignOut={() => logout()}
 * />
 * ```
 */
export function UserDropdown({
  user,
  role,
  isOnline = false,
  onNavigate,
  onSignOut,
  signOutLabel = null,
  className = '',
}: UserDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isConnectorAnimating, setIsConnectorAnimating] = useState(false);
  const [isRefineOpen, setIsRefineOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { width } = useBreakpoint();
  const isMobile = width < 480;

  const handleToggle = useCallback(() => {
    if (!isOpen && isMobile) {
      // Trigger connector animation on mobile
      setIsConnectorAnimating(true);
      setTimeout(() => setIsConnectorAnimating(false), 400);

      // Delay menu opening slightly to let beam start first
      setTimeout(() => setIsOpen(true), 80);
    } else {
      setIsOpen((prev) => !prev);
    }
  }, [isOpen, isMobile]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * Three rows are not navigation and are handled here.
   *
   * "Adjust Goals" opens the refine-plan surface, kept self-contained so the
   * global menu carries no dashboard/plan data, and "Invite to Dō" opens the
   * invite dialog. Sign Out is the caller's, since
   * only it holds the session. Everything else is a destination.
   */
  const handleSelect = useCallback(
    (item: NavItem) => {
      handleClose();

      if (item.id === SIGN_OUT) {
        onSignOut();
        return;
      }
      if (item.id === ADJUST_GOALS) {
        setIsRefineOpen(true);
        return;
      }
      if (item.id === INVITE) {
        setIsInviteOpen(true);
        return;
      }
      onNavigate(item.href);
    },
    [handleClose, onNavigate, onSignOut]
  );

  return (
    <div className={`relative ${className}`}>
      <UserDropdownTrigger
        ref={triggerRef}
        avatar={user.avatar}
        username={user.username}
        isOpen={isOpen}
        onClick={handleToggle}
        isMobile={isMobile}
      />

      {/* Mobile backdrop */}
      {isMobile && (
        <div
          className={`
            hidden max-[479px]:block
            fixed inset-0 z-[99]
            bg-scrim-strong backdrop-blur-scrim
            transition-all duration-300 ease-out
            ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}
          `}
          onClick={handleClose}
          aria-hidden="true"
        />
      )}

      {/* Mobile connector beam */}
      {isMobile && (
        <BottomSheetConnector
          isAnimating={isConnectorAnimating}
          rightOffset={24}
        />
      )}

      <UserDropdownMenu
        user={user}
        role={role}
        isOnline={isOnline}
        isOpen={isOpen}
        onClose={handleClose}
        onSelect={handleSelect}
        signOutLabel={signOutLabel}
        isMobile={isMobile}
        triggerRef={triggerRef}
      />

      <RefinePlanModal
        isOpen={isRefineOpen}
        onClose={() => setIsRefineOpen(false)}
      />

      <InviteModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
      />
    </div>
  );
}

export default UserDropdown;
